import { Stack, Typography } from "@altimateai/lego";
import { IconDatabaseOff } from "@tabler/icons-react";
import dayjs from "dayjs";
import DataTable from "@/components/tables/DataTable";
import { Paginate } from "@/components/paginate";
import { FilterApplicableFor } from "@/components/tags/types";
import { useClusters } from "../hooks";
import type { ClusterSummary, ClustersParams } from "../types";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import { useDateFromSearchParams } from "@/helpers/useTimeHooks";
import { useClusterListHeaders } from ".";

interface Props {
  workspaceId?: string;
  days?: number;
  filterPortalId: string;
  savedFiltersPortalId: string;
}

const ClusterList = ({ workspaceId, days = 30, filterPortalId, savedFiltersPortalId }: Props) => {
  const { searchParams, updateSearchParamsByKey } = useFiltersBySearchParams();
  const {
    headers,
    sortAttribute,
    sortOrder,
    hasFilters,
    clusterNameFilter,
    workspaceFilter,
    computeTypeFilter,
    enablePhotonFilter,
  } = useClusterListHeaders({ workspaceId });
  const { startDate, endDate } = useDateFromSearchParams();
  const startDateStr = dayjs(startDate).format("YYYY-MM-DD");
  const endDateStr = dayjs(endDate).format("YYYY-MM-DD");

  const page = parseInt(searchParams.get("page") || "0", 10);
  const pageSize = parseInt(searchParams.get("pageSize") || "20", 10);

  // The list is served from the cost mart, which carries compute_type directly,
  // so the Type filter is sent as compute_type. Cluster name + workspace are the
  // only other supported filters; source / node-type filtering and sorting by
  // inventory columns are intentionally unsupported (would hit the 49M-row
  // inventory table). Photon is the exception: the backend resolves it with a
  // one-off inventory join, so it filters (but doesn't sort).
  const params: ClustersParams = {
    workspace_id: workspaceId,
    days,
    start_date: startDateStr,
    end_date: endDateStr,
    page: page + 1,
    page_size: pageSize,
    cluster_names: clusterNameFilter.length > 0 ? clusterNameFilter : undefined,
    workspaces: workspaceFilter.length > 0 ? workspaceFilter : undefined,
    compute_type: computeTypeFilter.length > 0 ? computeTypeFilter : undefined,
    enable_photon: enablePhotonFilter === "" ? undefined : enablePhotonFilter === "true",
    sortAttribute,
    sortOrder,
  };

  const { data, isLoading, isFetching } = useClusters(params);
  const tableLoading = isLoading || isFetching;
  const tableData = { items: data?.clusters || [] };

  return (
    <>
      <Stack direction="row" align="center" justify="between" className="al-w-full">
        <div id={filterPortalId} />
        <div id={savedFiltersPortalId} />
      </Stack>
      <DataTable
        header={headers}
        data={tableData}
        isLoading={tableLoading}
        getRowUrl={(item: ClusterSummary) => `/clusters/${item.cluster_id}`}
        minWidth={1400}
        applicableFor={FilterApplicableFor.CLUSTERS}
        portals={{
          filter: filterPortalId,
          savedFilters: savedFiltersPortalId,
        }}
      />
      {!tableLoading && tableData.items.length === 0 ? (
        <Stack
          direction="column"
          align="center"
          justify="center"
          className="al-py-16 al-text-gray-500"
        >
          <IconDatabaseOff size={48} stroke={1.5} className="al-mb-4 al-text-gray-400" />
          <Typography className="al-text-gray-500">No clusters found</Typography>
          {hasFilters && (
            <Typography size="sm" className="al-mt-1 al-text-gray-400">
              Try adjusting your filters
            </Typography>
          )}
        </Stack>
      ) : (
        <Paginate
          itemCount={data?.total_count || 0}
          page={page}
          pageSize={pageSize}
          numPages={data?.total_pages || 1}
          onPageClick={newPage => updateSearchParamsByKey({ page: String(newPage) })}
          onPageSizeChange={newSize =>
            updateSearchParamsByKey({ pageSize: String(newSize), page: "0" })
          }
          showPageSize
          isLoading={tableLoading}
        />
      )}
    </>
  );
};

export default ClusterList;
